import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '@whinerp/client';
import { Button, Icon } from '@whinerp/ui';
import { useSession, SESSION_QUERY_KEY } from '../app/session.js';
import { useT } from '../app/i18n.js';

/**
 * Somebody from support is in this session (F-144).
 *
 * Above the navigation and above every screen, with no close button, because
 * the specification's own words are "never let staff act invisibly inside a
 * customer account". It is drawn from the session, which is re-read on every
 * load, so it cannot be dismissed by anything the browser does - there is no
 * flag to clear and no state to drop.
 *
 * It names the person, not the company. "Support is helping" is a sentence
 * nobody can follow up; "Sam Support <sam@…>" is a name to ask about.
 *
 * And it offers the end. Being watched and being unable to stop it are
 * different situations, and the person being visited should not have to find an
 * administrator to end a session they are sitting in.
 */
export function VisitBanner(): JSX.Element | null {
  const t = useT();
  const { session } = useSession();
  const queryClient = useQueryClient();

  const end = useMutation<{ ended: boolean }, ApiError>({
    mutationFn: () => api.endMySupportVisit({}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
  });

  const visit = session.visit;
  if (!visit) return null;

  return (
    <div
      data-region="support-visit"
      data-visit={visit.impersonationId}
      role="status"
      className="flex flex-wrap items-center gap-2 border-b border-b-rule bg-danger-tint px-gutter py-2 text-dense text-danger-text"
    >
      <Icon name="Eye" size="inline" decorative />
      <span data-visit-staff className="font-medium">
        {t('visit.who', { values: { staff: visit.staff } })}
      </span>
      {/* The reason they gave, shown to the person being visited rather than
          kept for an audit nobody reads. */}
      <span data-visit-reason className="min-w-0 text-ink-600">
        {t('visit.reason', { values: { reason: visit.reason } })}
      </span>
      <Button
        intent="secondary"
        size="small"
        className="ms-auto"
        data-visit-end
        onClick={() => end.mutate()}
      >
        {t('visit.end')}
      </Button>
    </div>
  );
}
